import { useState } from "react";
import { motion } from "framer-motion";
import { MicroLabel } from "@/components/decor/CropMarks";
import { Printer, Gift, Megaphone, Layers } from "lucide-react";

const PILLARS = [
  {
    label: "Print Media",
    accent: "#0072CE",
    icon: Printer,
    body:
      "Business cards, brochures, catalogues, flyers, packaging, labels, stationery, folders, and marketing collateral.",
    examples: ["Business cards", "Brochures", "Catalogues", "Stationery", "Packaging", "Labels"],
  },
  {
    label: "Corporate Gifting",
    accent: "#D6007F",
    icon: Gift,
    body:
      "Gift sets, hampers, office merchandise, apparel, bags, bottles, diaries, calendars, and premium branded utilities.",
    examples: ["Gift sets", "Hampers", "Apparel", "Bottles", "Diaries", "Calendars"],
  },
  {
    label: "Brand Visibility",
    accent: "#FFD200",
    icon: Megaphone,
    body:
      "Signage, flex, vinyl, sunboards, LED signage, acrylic boards, standees, event displays, and retail branding.",
    examples: ["Signage", "Vinyl", "Acrylic boards", "Standees", "Event displays", "Retail branding"],
  },
  {
    label: "Brand Development Support",
    accent: "#B89B5E",
    icon: Layers,
    body:
      "Logo application, stationery systems, marketing collaterals, packaging direction, digital-ready assets, and execution-based support.",
    examples: ["Logo application", "Stationery systems", "Collaterals", "Packaging", "Digital assets"],
  },
];

// Stylized piece SVG with subtle CMYK accent strip
const PieceVisual = ({ active, accent }) => (
  <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
    <defs>
      <linearGradient id={`g-${accent}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#1a1a1a" />
        <stop offset="1" stopColor="#0a0a0a" />
      </linearGradient>
    </defs>
    <path
      d="M10 10 H70 Q70 22 80 22 Q90 22 90 32 V46 H110 V110 H10 Z"
      fill={`url(#g-${accent})`}
      stroke={active ? accent : "rgba(192,192,192,0.35)"}
      strokeWidth={active ? 1.5 : 1}
    />
    <rect x="14" y="14" width="22" height="2" fill={accent} opacity={active ? 0.95 : 0.6} />
    <rect x="14" y="22" width="38" height="1" fill="rgba(245,241,232,0.45)" />
    <rect x="14" y="28" width="30" height="1" fill="rgba(245,241,232,0.3)" />
    <circle cx="98" cy="98" r="6" fill="none" stroke={accent} opacity={0.75} />
    <line x1="92" y1="98" x2="104" y2="98" stroke={accent} opacity={0.6} />
    <line x1="98" y1="92" x2="98" y2="104" stroke={accent} opacity={0.6} />
  </svg>
);

const PuzzlePillars = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      data-testid="puzzle-pillars"
      className="relative"
      style={{ background: "#0A0A0A", paddingTop: 120, paddingBottom: 120 }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <MicroLabel index={4} accent="#B89B5E">
              Signature System
            </MicroLabel>
            <h2
              className="mt-6"
              style={{
                fontFamily: "Montserrat",
                fontWeight: 800,
                color: "#F5F1E8",
                fontSize: "clamp(34px, 5vw, 64px)",
                lineHeight: 0.98,
                letterSpacing: "-0.025em",
                textTransform: "uppercase",
              }}
            >
              Four Pieces.
              <br />
              <span style={{ color: "#B89B5E" }}>One brand presence.</span>
            </h2>
          </div>
          <p
            style={{
              color: "#C0C0C0",
              maxWidth: 420,
              fontSize: 14,
              lineHeight: 1.75,
            }}
          >
            The Krister mark assembles from four engineered modules — each one a
            production discipline. Click a piece to inspect the system.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Pieces grid */}
          <div className="lg:col-span-6">
            <div
              className="grid grid-cols-2 gap-px"
              style={{ background: "rgba(192,192,192,0.15)" }}
            >
              {PILLARS.map((p, i) => {
                const isActive = active === i;
                const Icon = p.icon;
                return (
                  <button
                    key={p.label}
                    data-testid={`pillar-piece-${i}`}
                    type="button"
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    className="relative text-left transition-all duration-500 group"
                    style={{
                      background: isActive ? "#141414" : "#0A0A0A",
                      padding: "24px 22px 26px",
                      minHeight: 240,
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <span
                        className="ko-overline"
                        style={{ color: p.accent, fontSize: 10 }}
                      >
                        {String(i + 1).padStart(2, "0")} · Module
                      </span>
                      <Icon size={18} strokeWidth={1.5} color={isActive ? p.accent : "#C0C0C0"} />
                    </div>
                    <div className="mt-6 w-24 h-24 md:w-28 md:h-28">
                      <PieceVisual active={isActive} accent={p.accent} />
                    </div>
                    <h3
                      className="mt-5"
                      style={{
                        fontFamily: "Montserrat",
                        fontWeight: 700,
                        color: "#F5F1E8",
                        fontSize: "clamp(14px, 1.3vw, 18px)",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {p.label}
                    </h3>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-6 flex flex-col">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="ko-card relative p-8 md:p-10 flex-1"
              style={{
                background: "#141414",
                border: "1px solid rgba(192,192,192,0.15)",
                minHeight: 320,
              }}
            >
              <span
                aria-hidden
                className="absolute top-0 left-0"
                style={{ width: 48, height: 4, background: PILLARS[active].accent }}
              />
              <span
                className="ko-overline"
                style={{ color: PILLARS[active].accent }}
              >
                Pillar 0{active + 1}
              </span>
              <h3
                className="mt-6"
                style={{
                  fontFamily: "Montserrat",
                  fontWeight: 800,
                  color: "#F5F1E8",
                  fontSize: "clamp(28px, 3.4vw, 44px)",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                  textTransform: "uppercase",
                }}
              >
                {PILLARS[active].label}
              </h3>
              <p
                className="mt-6"
                style={{
                  color: "#C0C0C0",
                  fontSize: 15,
                  lineHeight: 1.75,
                  maxWidth: 540,
                }}
              >
                {PILLARS[active].body}
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {PILLARS[active].examples.map((ex) => (
                  <span
                    key={ex}
                    className="ko-overline"
                    style={{
                      color: "#F5F1E8",
                      fontSize: 10,
                      padding: "8px 12px",
                      border: "1px solid rgba(192,192,192,0.25)",
                    }}
                  >
                    {ex}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PuzzlePillars;
